import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  signal,
  OnInit,
  ViewChild,
  Inject,
  computed,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { TagRatingDonutComponent } from '@app/tags/components/tag-rating-donut/tag-rating-donut.component';
import { twMerge } from '@degreed/apollo-angular';
import { IconComponent } from '@degreed/apollo-angular';
import { TranslateFn, translateWithDefaults } from '@app/shared/utils';
import { TagsService } from '@app/tags/services/tags.service';
import { AuthService } from '@app/shared/services/auth.service';
import { LDFlagsService } from '@dg/shared-services';
import { DIALOG_DATA } from '@angular/cdk/dialog';
import { TagRatingService } from '@app/tags/services/tag-rating.service';
import { TagsApi } from '@app/tags/tag-api.model';

import {
  ReactiveFormsModule,
  FormGroup,
  FormBuilder,
  ValidatorFn,
  ValidationErrors,
  AbstractControl,
} from '@angular/forms';
import { TagModalService } from '@app/tags/services/tag-modal.service';
import { SkeletonComponent } from '@app/skeleton/skeleton.component';
@Component({
  selector: 'dgx-edit-profile-skill-rating-modal-v3',
  templateUrl: './edit-profile-skill-rating-modal-v3.component.html',
  standalone: true,
  imports: [
    TagRatingDonutComponent,
    IconComponent,
    ReactiveFormsModule,
    SkeletonComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditProfileSkillRatingModalV3 implements OnInit, AfterViewInit {
  @ViewChild('titleElement') public titleElement!: ElementRef;
  public ratingsForm: FormGroup;

  public isTarget = false;
  public tagId: number = 1;
  public currentRating: number = 1;
  public expanded = signal(false);
  public isClamped = signal(false);
  public twMerge = twMerge;
  public isPopoverOpen = false;
  public translate: TranslateFn;
  public clearSelectionFlag = signal(false);
  public currentRatingObject = signal(null);
  public ratingLevels = signal([]);
  public tag = signal<TagsApi.Tag | null>(null);
  public onClose: (arg: unknown) => unknown;

  public ModalTitle = computed(() => {
    if (this.isTarget) {
      return this.translate(
        'Set a skill target for ' + this.tag()?.title,
        'Core_TargetSkillRating_Modal_Title',
        { skill: this.tag()?.title }
      );
    } else {
      return this.translate(
        'Rate yourself for ' + this.tag()?.title,
        'Core_SelfSkillRating_Modal_Title',
        { skill: this.tag()?.title }
      );
    }
  });
  public ModalDescription = computed(() => this.tag()?.description ?? 'N/A');
  public skillLabel = computed(() =>
    this.isTarget
      ? this.translate(
          'Select Skill Target',
          'PersonalSkillView_SkillTargetTitle'
        )
      : this.translate(
          'Select Self-rating',
          'PersonalSkillView_SkillSelfRatingTitle'
        )
  );
  constructor(
    private el: ElementRef,
    public translateService: TranslateService,
    public tagsService: TagsService,
    public authService: AuthService,
    public ldFlagsService: LDFlagsService,
    public tagService: TagRatingService,
    private tagModalService: TagModalService,
    private fb: FormBuilder,
    @Inject(DIALOG_DATA)
    public data: {
      hidePrivacyChange: boolean;
      isTarget: boolean;
      tag: TagsApi.Tag;
      currentRating: number;
      onClose: (arg: unknown) => unknown;
    }
  ) {
    this.ratingsForm = this.fb.group({
      level: ['', this.radioOrClearValidator()], // Initially empty (no selection)
    });
    this.translate = translateWithDefaults(this.translateService);
  }
  public ngOnInit(): void {
    this.isTarget = this.data.isTarget ?? this.isTarget;
    this.currentRating = this.data.currentRating ?? this.currentRating;
    this.onClose = this.data.onClose;
    this.tag.set(this.data.tag);
    if (
      this.authService.authUser?.defaultOrgInfo?.settings
        .enableSkillsPlatform &&
      this.ldFlagsService.degreedSkillsOctober2024
    ) {
      this.tagsService
        .getTagLevelDescriptions(this.tagId)
        .subscribe(
          (levels: {
            isDefault: boolean;
            skillLevels: { skillLevel: string; levelDescription: string }[];
          }) => {
            this.ratingLevels.set(
              levels.skillLevels.map((level, i) => ({
                name: level.skillLevel,
                description: level.levelDescription,
                value: i + 1,
              }))
            );
            this.initializeLevels();
          }
        );
    } else {
      this.ratingLevels.set(this.tagsService.orgLevels);
      this.initializeLevels();
    }
  }
  public radioOrClearValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value || this.clearSelectionFlag()) {
        return null; // Valid if a radio is selected or "Clear" is pressed
      }
      return { radioRequired: true }; // Error if neither is true
    };
  }
  public initializeLevels() {
    const currentRating = Number(this.tag().rating?.level);
    this.currentRatingObject.set(
      this.ratingLevels().find((d) => d.value === currentRating)
    );
  }
  public ngAfterViewInit() {
    // setTimeout(() => this.vm.tagSummary.titleElement.nativeElement.focus());
    const textElement = this.el.nativeElement.querySelector('.tw-text-content');
    if (textElement.scrollHeight > textElement.clientHeight) {
      this.isClamped.set(true);
    }
  }

  public clearSelection(event): void {
    event.preventDefault();
    this.clearSelectionFlag.set(true);
    this.ratingsForm.patchValue({ level: null });
  }
  public clearCurrentSelection(event): void {
    event.preventDefault();
    this.ratingsForm.patchValue({ level: null });
  }
  public submitRating() {
    this.tagModalService.openPreviousDialog();
    if (this.onClose) {
      this.onClose(this.ratingsForm.value);
    }
  }
  public dimissRating() {
    this.tagModalService.openPreviousDialog();
    if (this.onClose) {
      this.onClose(null);
    }
  }
}
